/* eslint-disable no-unused-expressions */
import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { Utils } from "Utils/utils";
import makeApi, { URLS } from "Utils/apiURL";
import { PAYWALL_CONTENT } from "Model/model";
import { ZOHO_OAUTH_URL } from "Static/constant";
import zohoIcon from "Assets/png/zohoCrm.png";
import { ReactComponent as ThunderOrangeIcon } from "Assets/svg/thunderOrange.svg";
import { toasterState } from "Components/common/toaster";
import UpgradeTooltip from "Components/Paywall/UpgradeTooltip";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import FormActionButton from "../../ConnectInbox/Configure/FormActionButton";
import ConnectOrDisconnectCard from "../ConnectOrDisconnectCard";
import ZohoTabFields from "./ZohoTabFields";
import { tagsStoreData } from "Stores/TagsData";
import { MXP_EVENT } from "Static/MixPanelEvents";
import { zohoDataStore } from "./ZohoStoreData";

const defaultFunc = () => { };
const ZohoConfigure = observer(props => {
  const {
    hideCbk = () => { },
    isFeatureEnabled = true,
    showAdminPopup = defaultFunc,
    infreePlan = false,
    planName = ""
  } = props;
  const { zohoData: { zohoConnected = false, crmSettings = {} } = {} } =
    zohoDataStore;
  const { instanceUrl = "" } = crmSettings || {};
  const updatedZohoConnected =
    zohoConnected && crmSettings && Object.keys(crmSettings).length > 0;

  const [removeAccountLoader, setRemoveAccountLoader] = useState(false);
  const [showUpgradeTp, setShowUpgradeTp] = useState(false);
  const [saveChangesLoader, setSaveChangesLoader] = useState(false);

  const openZohoOauthWindow = () => {
    if (ZOHO_OAUTH_URL) {
      const w = 600;
      const h = 800;
      const left = window.screen.width / 2 - w / 2;
      const top = window.screen.height / 2 - h / 2;
      const uuid = Utils.uuid();
      storeStateValueInSession(uuid);
      const url = ZOHO_OAUTH_URL + "&state=" + uuid;
      const params = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=${w},height=${h},left=${left},top=${top}`;
      window.open(url, "Zoho Login", params);
      const oauthCbk = async ev => {
        if (ev?.data?.type === "ZOHO_CONNECT_OAUTH") {
          // debugger
          clearStateValueFromSession();
          window.removeEventListener("message", oauthCbk);
          if (ev?.data?.code?.toString() === "200") {
            toasterState.setToastMsg(
              "Zoho account connected successfully",
              "success"
            );
            Utils.mixpanelTrack(MXP_EVENT.CRM_CONNECTED, {
              type: "Zoho"
            });
            zohoDataStore.getZohoData();
          } else {
            toasterState.setToastMsg(
              "Something went wrong. Please try again later",
              "fail"
            );
          }
        }
      };
      window.addEventListener("message", oauthCbk);
    }
  };

  const removeAccount = () => {
    const cbk = async () => {
      setRemoveAccountLoader(true);
      await zohoDataStore.removeZohoAccount();
      setRemoveAccountLoader(false);
    };

    if (Utils.isAdmin()) {
      confirmationPopupState.setPopupValues({
        content:
          "Are you sure? Disconnecting the CRM will stop all current and scheduled sync jobs.",
        actionBtnText: "Disconnect",
        cancelBtnText: "Cancel",
        callback: () => {
          cbk();
        }
      });
      confirmationPopupState.setShowPopup(true);
    } else {
      showAdminPopup("zoho");
    }
  };

  const makeAction = () => {
    !updatedZohoConnected ? openZohoOauthWindow() : removeAccount();
  };

  const removeUnwantedFields = (config = {}, setConfig = () => { }) => {
    const fields = [...config?.fields];
    fields?.length > 0 &&
      fields.map((item, index) => {
        if (!item?.sgField && !item?.crmField) {
          fields.splice(index, 1);
        }
        return fields;
      });
    setConfig({ ...config, fields });
    return {
      ...config,
      fields
    };
  };

  const constructZohoConfig = (isFromSave = false) => {
    const extractedLeadTags =
      Utils.extractTagNameFromList(zohoDataStore.zohoLeadTags) || [];
    const extractedContactTags =
      Utils.extractTagNameFromList(zohoDataStore.zohoContactTags) || [];

    const updatedZohoLeadConfig = isFromSave
      ? removeUnwantedFields(
        zohoDataStore?.zohoLeadConfig,
        zohoDataStore.setZohoLeadConfig
      )
      : zohoDataStore?.zohoLeadConfig;
    const updatedZohoContactConfig = isFromSave
      ? removeUnwantedFields(
        zohoDataStore?.zohoContactConfig,
        zohoDataStore.setZohoContactConfig
      )
      : zohoDataStore?.zohoContactConfig;
    const payload = {
      config: {
        contact: {
          ...updatedZohoContactConfig,
          tags: extractedContactTags,
          ...(zohoDataStore.zohoContactLists?.length > 0 && {
            listIds: zohoDataStore.zohoContactLists
          })
        },
        lead: {
          ...updatedZohoLeadConfig,
          tags: extractedLeadTags,
          ...(zohoDataStore.zohoLeadLists?.length > 0 && {
            listIds: zohoDataStore.zohoLeadLists
          })
        }
      },
      owner: zohoDataStore?.zohoOwnerConfig,
      preference: {
        syncSentEnabled: true,
        syncOpenEnabled: true,
        syncClickEnabled: true,
        syncReplyEnabled: true,
        ...zohoDataStore?.zohoPreferences
      }
    };
    return payload;
  };

  const saveZohoConfigure = async () => {
    if (Utils.isAdmin()) {
      const saveChangesBtnDisabled =
        !(updatedZohoConnected && isFeatureEnabled) ||
        zohoDataStore.disableSaveButton;
      if (!saveChangesBtnDisabled) {
        setSaveChangesLoader(true);
        const payload = constructZohoConfig(true);
        try {
          const config = {
            url: URLS.updateZohoConfig,
            method: "POST",
            data: payload
          };
          let res = await makeApi(config);
          if (res?.data) {
            tagsStoreData.getAllTags();
            zohoDataStore.setZohoData(res?.data);
            setSaveChangesLoader(false);
            toasterState.setToastMsg(
              "Zoho Integration settings updated successfully.",
              "success"
            );
          } else {
            Utils.showApiFailureResponse(res);
          }
        } catch (err) {
          console.error("Zoho Owners listing error", err);
        }
      }
    } else {
      showAdminPopup();
    }
  };

  const storeStateValueInSession = (stateVal = "") => {
    sessionStorage?.setItem("zohoOAuthState", stateVal);
  };

  const clearStateValueFromSession = () => {
    sessionStorage?.removeItem("zohoOAuthState");
  };

  useEffect(() => {
    const {
      config: { lead = {}, contact = {} } = {},
      owner = [],
      syncClickEnabled = false,
      syncOpenEnabled = false,
      syncPhoneCallEnabled = true,
      syncReplyEnabled = false,
      syncSentEnabled = false,
      syncLiAcceptRequest = false,
      syncLiSendRequest = false,
      syncLiMessageSent = false,
      syncLiReplyReceived = false,
      syncCustomTask = false
    } = crmSettings || {};
    if (lead && Object.keys(lead).length > 0) {
      const leadConfig = Utils.createObjWithoutRef(lead);
      zohoDataStore.setZohoLeadConfig(leadConfig);
      if (lead?.listIds?.length > 0) {
        zohoDataStore.setZohoLeadLists(lead.listIds);
      } else {
        zohoDataStore.setZohoLeadLists([]);
      }
    }
    if (contact && Object.keys(contact).length > 0) {
      const contactConfig = Utils.createObjWithoutRef(contact);
      zohoDataStore.setZohoContactConfig(contactConfig);
      if (contact?.listIds?.length > 0) {
        zohoDataStore.setZohoContactLists(contact.listIds);
      } else {
        zohoDataStore.setZohoContactLists([]);
      }
    }
    zohoDataStore.setZohoOwnerConfig(owner);
    zohoDataStore.setZohoPreferences({
      syncClickEnabled,
      syncOpenEnabled,
      syncPhoneCallEnabled,
      syncReplyEnabled,
      syncSentEnabled,
      syncLiAcceptRequest,
      syncLiSendRequest,
      syncLiMessageSent,
      syncLiReplyReceived,
      syncCustomTask
    });
  }, [crmSettings?.config]);

  return (
    <div className="crmConfigurePage zoho">
      <ConnectOrDisconnectCard
        type="Zoho"
        icon={zohoIcon}
        addOrRemoveAccount={makeAction}
        connectedEmail={instanceUrl?.replace("https://", "")}
        isConnected={updatedZohoConnected}
        removeLoader={removeAccountLoader}
      />
      {updatedZohoConnected && (
        <div
          className="activityLogWrapper"
          onMouseEnter={() => !isFeatureEnabled && setShowUpgradeTp(true)}
          onMouseLeave={() => !isFeatureEnabled && setShowUpgradeTp(false)}
        >
          <ZohoTabFields
            className={`${!isFeatureEnabled ? "disabledConfigure" : ""}`}
            crmSettings={crmSettings}
            constructZohoConfig={constructZohoConfig}
          />
          {!isFeatureEnabled && <ThunderOrangeIcon className="thunderOrange" />}

          {showUpgradeTp &&
            !isFeatureEnabled &&
            (infreePlan ||
              ["pro", "basic"].includes(planName?.toLowerCase())) && (
              <UpgradeTooltip
                {...PAYWALL_CONTENT[
                infreePlan || planName?.toLowerCase() === "basic"
                  ? "zohoSettings"
                  : "zohoSettingsUnlimited"
                ]}
              />
            )}
        </div>
      )}
      <FormActionButton
        cancel={() => hideCbk()}
        save={saveZohoConfigure}
        saveBtnText="Save changes"
        loader={saveChangesLoader}
        disabled={
          !(updatedZohoConnected && isFeatureEnabled) ||
          zohoDataStore.disableSaveButton
        }
      />
    </div>
  );
});

export { ZohoConfigure };
export default ZohoConfigure;
